import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { GridMap } from "../generation/GridMap";
import { findPath } from "../generation/Pathfinding";
import type { Point } from "../generation/types";
import { gridToWorld, worldToGrid } from "../rendering/worldCoordinates";

export class EnemyNavigation {
  private path: Point[] = [];
  private pathIndex = 0;
  private refreshRemaining = 0;
  private targetCell?: Point;
  private readonly movement = Vector3.Zero();

  public constructor(private readonly grid: GridMap) {}

  public update(
    deltaSeconds: number,
    position: Vector3,
    target: Vector3,
    movementSpeed: number,
  ): Vector3 {
    const targetCell = worldToGrid(target.x, target.z, this.grid);
    this.refreshRemaining -= deltaSeconds;
    if (
      this.refreshRemaining <= 0 ||
      !this.targetCell ||
      Math.abs(targetCell.x - this.targetCell.x) + Math.abs(targetCell.y - this.targetCell.y) >= 2
    ) {
      this.path = findPath(this.grid, worldToGrid(position.x, position.z, this.grid), targetCell);
      this.pathIndex = Math.min(1, Math.max(0, this.path.length - 1));
      this.targetCell = targetCell;
      this.refreshRemaining = 0.55 + Math.random() * 0.25;
    }
    const nextCell = this.path[this.pathIndex];
    if (!nextCell) return this.movement.setAll(0);
    const nextWorld = gridToWorld(nextCell, this.grid);
    const offsetX = nextWorld.x - position.x;
    const offsetZ = nextWorld.z - position.z;
    const distanceSquared = offsetX * offsetX + offsetZ * offsetZ;
    if (distanceSquared < 0.18) {
      this.pathIndex += 1;
      return this.movement.setAll(0);
    }
    const movementScale = (movementSpeed * deltaSeconds) / Math.sqrt(distanceSquared);
    return this.movement.set(offsetX * movementScale, 0, offsetZ * movementScale);
  }

  public clear(): void {
    this.path = [];
    this.pathIndex = 0;
    this.targetCell = undefined;
  }
}

export function selectEnemyNavigationTarget(
  seesPlayer: boolean,
  playerPosition: Vector3,
  lastKnownPlayerPosition: Vector3,
): Vector3 {
  return seesPlayer ? playerPosition : lastKnownPlayerPosition;
}
